import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSupportingText,
  AdminMetric,
  AdminMetricDelta,
  AdminWidgetTitle,
  AdminListFilterDropdown,
  AdminFormSecondaryAction,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_SECTION_OFFSET_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
  ADMIN_CONTENT_PANEL_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import * as echarts from 'echarts';

const formatMetric = (value) => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString();
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  return `$${value.toLocaleString()}`;
};

const renderDelta = (change) => {
  if (change === null || change === undefined) return null;
  return (
    <div className="mt-1">
      <AdminMetricDelta value={change} />
    </div>
  );
};

const TIMEFRAME_OPTIONS = ['Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Custom Range'];

// ECharts Component Wrapper
const EChartComponent = ({ option, style = { width: '100%', height: '300px' } }) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !option) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
    }

    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;
    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, [option]);

  return <div ref={chartRef} style={style} />;
};

// Generate mock data for finance reports
const generateMockData = (timeframe, periodType = 'Daily') => {
  const now = new Date();
  const days = timeframe === 'Last 7 Days' ? 7 : timeframe === 'Last 30 Days' ? 30 : 90;
  
  // Generate revenue data (daily or monthly)
  const revenueData = [];
  const refundsData = [];
  const disputesData = [];
  
  const intervals = periodType === 'Daily' ? days : Math.ceil(days / 30);
  const increment = periodType === 'Daily' ? 1 : 30;
  
  for (let i = intervals - 1; i >= 0; i--) {
    const date = new Date(now);
    if (periodType === 'Daily') {
      date.setDate(date.getDate() - i);
    } else {
      date.setDate(date.getDate() - (i * 30));
    }
    
    const dateLabel = periodType === 'Daily' 
      ? date.toISOString().split('T')[0].slice(5)
      : `${date.getMonth() + 1}/${date.getFullYear()}`;
    
    revenueData.push({
      date: date.toISOString().split('T')[0],
      dateLabel,
      gross: Math.floor(Math.random() * 8000) + 5000,
      net: Math.floor(Math.random() * 7500) + 4500,
    });
    
    refundsData.push({
      date: date.toISOString().split('T')[0],
      dateLabel,
      amount: Math.floor(Math.random() * 500) + 100,
    });
    
    disputesData.push({
      date: date.toISOString().split('T')[0],
      dateLabel,
      count: Math.floor(Math.random() * 10) + 2,
    });
  }

  // Revenue by Plan
  const revenueByPlan = [
    { plan: 'Basic', amount: 45230, percentage: 26.1 },
    { plan: 'Pro', amount: 128450, percentage: 73.9 },
  ];

  // Subscription Status Distribution
  const subscriptionStatus = {
    'Active': 1245,
    'Past Due': 87,
    'Cancelled': 234,
  };

  // Payment Outcomes
  const paymentOutcomes = {
    'Succeeded': 1845,
    'Failed': 89,
    'Refunded': 34,
    'Disputed': 12,
  };

  // Net vs Gross Revenue (for stacked bar - last 6 periods)
  const netVsGross = revenueData.slice(-6).map(item => ({
    period: item.dateLabel,
    gross: item.gross,
    net: item.net,
  }));

  return {
    revenueData,
    refundsData,
    disputesData,
    revenueByPlan,
    subscriptionStatus,
    paymentOutcomes,
    netVsGross,
  };
};

const BillingReports = () => {
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState('Last 30 Days');
  const [timeframeOpen, setTimeframeOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [periodType, setPeriodType] = useState('Daily'); // 'Daily' or 'Monthly'

  const timeframeRef = useRef(null);

  useClickOutside(timeframeRef, () => setTimeframeOpen(false));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const data = generateMockData(timeframe, periodType);
        setReportData(data);
      } catch (error) {
        console.error('Error fetching finance reports:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeframe, periodType]);

  // Calculate Summary Indicators
  const summaryIndicators = useMemo(() => {
    if (!reportData) return null;

    const totalGross = reportData.revenueData.reduce((sum, item) => sum + item.gross, 0);
    const totalNet = reportData.revenueData.reduce((sum, item) => sum + item.net, 0);
    const totalRefunds = reportData.refundsData.reduce((sum, item) => sum + item.amount, 0);
    const totalTransactions = Object.values(reportData.paymentOutcomes).reduce((sum, val) => sum + val, 0);
    const refundRate = totalTransactions > 0 ? ((reportData.paymentOutcomes.Refunded / totalTransactions) * 100).toFixed(1) : 0;
    const disputeRate = totalTransactions > 0 ? ((reportData.paymentOutcomes.Disputed / totalTransactions) * 100).toFixed(1) : 0;

    return {
      grossRevenue: totalGross,
      netRevenue: totalNet,
      activeSubscriptions: reportData.subscriptionStatus.Active,
      newSubscriptions: 156, // Mock value for period
      refundRate: parseFloat(refundRate),
      disputeRate: parseFloat(disputeRate),
    };
  }, [reportData]);

  // Revenue Over Time Chart (Main Trend)
  const revenueTrendOption = useMemo(() => {
    if (!reportData) return null;

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const data = params[0];
          return `${data.axisValue}<br/>Revenue: $${data.value.toLocaleString()}`;
        },
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 40,
        left: 60,
      },
      xAxis: {
        type: 'category',
        data: reportData.revenueData.map(d => d.dateLabel),
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value) => `$${value.toLocaleString()}`,
        },
      },
      series: [
        {
          name: 'Revenue',
          type: 'line',
          data: reportData.revenueData.map(d => d.net),
          smooth: true,
          lineStyle: { color: '#1E40AF', width: 2 },
          areaStyle: { color: 'rgba(30, 64, 175, 0.1)' },
        },
      ],
    };
  }, [reportData]);

  // Revenue by Plan Chart (Donut)
  const revenueByPlanOption = useMemo(() => {
    if (!reportData) return null;

    const data = reportData.revenueByPlan.map(item => ({
      name: item.plan,
      value: item.amount,
    }));

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: '{b}: ${c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
      },
      series: [
        {
          name: 'Revenue by Plan',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: data.map((item, index) => ({
            ...item,
            itemStyle: { color: index === 0 ? '#1E40AF' : '#6366F1' },
          })),
        },
      ],
    };
  }, [reportData]);

  // Refunds & Disputes Over Time Chart
  const refundsDisputesOption = useMemo(() => {
    if (!reportData) return null;

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
      },
      legend: {
        data: ['Refund Amount', 'Dispute Count'],
        top: 10,
      },
      grid: {
        top: 50,
        right: 20,
        bottom: 40,
        left: 60,
      },
      xAxis: {
        type: 'category',
        data: reportData.refundsData.map(d => d.dateLabel),
      },
      yAxis: [
        {
          type: 'value',
          name: 'Refund Amount (AUD)',
          position: 'left',
          axisLabel: {
            formatter: (value) => `$${value}`,
          },
        },
        {
          type: 'value',
          name: 'Dispute Count',
          position: 'right',
        },
      ],
      series: [
        {
          name: 'Refund Amount',
          type: 'line',
          yAxisIndex: 0,
          data: reportData.refundsData.map(d => d.amount),
          lineStyle: { color: '#F59E0B', width: 2 },
          itemStyle: { color: '#F59E0B' },
        },
        {
          name: 'Dispute Count',
          type: 'line',
          yAxisIndex: 1,
          data: reportData.disputesData.map(d => d.count),
          lineStyle: { color: '#EF4444', width: 2 },
          itemStyle: { color: '#EF4444' },
        },
      ],
    };
  }, [reportData]);

  // Subscription Status Distribution Chart (Donut)
  const subscriptionStatusOption = useMemo(() => {
    if (!reportData) return null;

    const data = Object.entries(reportData.subscriptionStatus).map(([name, value]) => ({
      name,
      value,
    }));

    const colors = {
      'Active': '#10B981',
      'Past Due': '#F59E0B',
      'Cancelled': '#6B7280',
    };

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
      },
      legend: {
        orient: 'vertical',
        right: 10,
        top: 'center',
      },
      series: [
        {
          name: 'Subscription Status',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: {
            show: false,
            position: 'center',
          },
          emphasis: {
            label: {
              show: true,
              fontSize: 16,
              fontWeight: 'bold',
            },
          },
          labelLine: {
            show: false,
          },
          data: data.map(item => ({
            ...item,
            itemStyle: { color: colors[item.name] || '#6B7280' },
          })),
        },
      ],
    };
  }, [reportData]);

  // Payment Outcomes Chart (Bar)
  const paymentOutcomesOption = useMemo(() => {
    if (!reportData) return null;

    const data = Object.entries(reportData.paymentOutcomes);

    const colors = {
      'Succeeded': '#10B981',
      'Failed': '#EF4444',
      'Refunded': '#F59E0B',
      'Disputed': '#6B7280',
    };

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      grid: {
        top: 20,
        right: 20,
        bottom: 40,
        left: 50,
      },
      xAxis: {
        type: 'category',
        data: data.map(([key]) => key),
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          name: 'Count',
          type: 'bar',
          data: data.map(([key, value]) => ({
            value,
            itemStyle: { color: colors[key] || '#6B7280' },
          })),
        },
      ],
    };
  }, [reportData]);

  // Net vs Gross Revenue Chart (Stacked Bar)
  const netVsGrossOption = useMemo(() => {
    if (!reportData) return null;

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
      },
      legend: {
        data: ['Gross Revenue', 'Net Revenue'],
        top: 10,
      },
      grid: {
        top: 50,
        right: 20,
        bottom: 40,
        left: 60,
      },
      xAxis: {
        type: 'category',
        data: reportData.netVsGross.map(item => item.period),
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value) => `$${value.toLocaleString()}`,
        },
      },
      series: [
        {
          name: 'Gross Revenue',
          type: 'bar',
          stack: 'revenue',
          data: reportData.netVsGross.map(item => item.gross),
          itemStyle: { color: '#1E40AF' },
        },
        {
          name: 'Net Revenue',
          type: 'bar',
          stack: 'revenue',
          data: reportData.netVsGross.map(item => item.net),
          itemStyle: { color: '#6366F1' },
        },
      ],
    };
  }, [reportData]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText className="text-offblack">Loading billing reports...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        {/* Heading */}
        <AdminPageTitle>Billing Reports</AdminPageTitle>

        {/* Search and Filters Row */}
        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-3 w-full lg:w-auto">
              <AdminFormSecondaryAction
                type="button"
                onClick={() => navigate('/admin/billing/transactions')}
              >
                View Transactions
              </AdminFormSecondaryAction>
            </div>

            {/* Filters - Right */}
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Date Range"
                value={timeframe}
                options={TIMEFRAME_OPTIONS.map((option) => ({ value: option, label: option }))}
                onChange={setTimeframe}
                menuRef={timeframeRef}
                open={timeframeOpen}
                setOpen={setTimeframeOpen}
              />
            </div>
          </div>
        </div>

        {/* Summary Indicators - ROW 1 (6 KPIs) */}
        {summaryIndicators && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 ${ADMIN_SECTION_OFFSET_CLASS}`}>
            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Gross Revenue</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mb-2 block">{formatCurrency(summaryIndicators.grossRevenue)}</AdminMetric>
              {renderDelta(12.5)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Net Revenue</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mb-2 block">{formatCurrency(summaryIndicators.netRevenue)}</AdminMetric>
              {renderDelta(11.2)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Active Subscriptions</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(summaryIndicators.activeSubscriptions)}</AdminMetric>
              {renderDelta(8.3)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">New Subscriptions</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(summaryIndicators.newSubscriptions)}</AdminMetric>
              {renderDelta(15.7)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Refund Rate</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mb-2 block">{summaryIndicators.refundRate}%</AdminMetric>
              {renderDelta(-0.5)}
            </DashboardMainContainer>

            <DashboardMainContainer className={`${ADMIN_METRIC_PANEL_CLASS} relative`}>
              <AdminSupportingText className="text-offblack mb-2">Dispute Rate</AdminSupportingText>
              <AdminMetric size="large" className="text-offblack mb-2 block">{summaryIndicators.disputeRate}%</AdminMetric>
              {renderDelta(-1.2)}
            </DashboardMainContainer>
          </div>
        )}

        {/* Revenue Over Time - ROW 2 (Full Width) */}
        <DashboardMainContainer className={`${ADMIN_CONTENT_PANEL_CLASS} ${ADMIN_SECTION_OFFSET_CLASS}`}>
          <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
            <AdminWidgetTitle className="text-offblack">
              Revenue Over Time
            </AdminWidgetTitle>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPeriodType('Daily')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  periodType === 'Daily'
                    ? 'bg-primaryblue text-white'
                    : 'bg-white text-offblack border border-lightgray hover:border-primaryblue'
                }`}
              >
                Daily
              </button>
              <button
                onClick={() => setPeriodType('Monthly')}
                className={`px-3 py-1 rounded text-xs transition-colors ${
                  periodType === 'Monthly'
                    ? 'bg-primaryblue text-white'
                    : 'bg-white text-offblack border border-lightgray hover:border-primaryblue'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>
          <div className="flex-1 p-4">
            {revenueTrendOption && (
              <>
                <EChartComponent option={revenueTrendOption} style={{ width: '100%', height: '350px' }} />
                <AdminSupportingText className="text-mediumgray mt-4 text-center">
                  Revenue compared to previous period
                </AdminSupportingText>
              </>
            )}
          </div>
        </DashboardMainContainer>

        {/* Second Row - Split Charts (2 side-by-side) */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${ADMIN_SECTION_OFFSET_CLASS}`}>
          {/* Revenue by Plan */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Revenue by Plan
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {revenueByPlanOption && (
                <EChartComponent option={revenueByPlanOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>

          {/* Refunds & Disputes Over Time */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Refunds & Disputes Over Time
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {refundsDisputesOption && (
                <EChartComponent option={refundsDisputesOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>
        </div>

        {/* Third Row - Distributions (3 widgets) */}
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 ${ADMIN_SECTION_OFFSET_CLASS}`}>
          {/* Subscription Status Distribution */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Subscription Status Distribution
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {subscriptionStatusOption && (
                <EChartComponent option={subscriptionStatusOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>

          {/* Payment Outcomes */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Payment Outcomes
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {paymentOutcomesOption && (
                <EChartComponent option={paymentOutcomesOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>

          {/* Net vs Gross Revenue */}
          <DashboardMainContainer className={ADMIN_CONTENT_PANEL_CLASS}>
            <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
              <AdminWidgetTitle className="text-offblack">
                Net vs Gross Revenue
              </AdminWidgetTitle>
            </div>
            <div className="flex-1 p-4">
              {netVsGrossOption && (
                <EChartComponent option={netVsGrossOption} style={{ width: '100%', height: '300px' }} />
              )}
            </div>
          </DashboardMainContainer>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BillingReports;
